import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'

import { cachedCan } from '@/features/auth/session'

import { RECORD_ROUTE } from './landing'

/**
 * What a hand-over route's `beforeLoad` is handed, of the router's context and params.
 *
 * Either id: a booking's screens carry `bookingId`, and a walk-in's carry `driveId`, because a
 * walk-in writes no `bookings` row and has none (ADR 0005). Both are optional here so one gate
 * stands in front of both families rather than two copies of one rule.
 */
type GateInput = {
  context: { queryClient: QueryClient }
  params: { bookingId?: string; driveId?: string }
}

type Gate = (input: GateInput) => void

/**
 * The gate every hand-over route stands behind: `sell` or above, else the record.
 *
 * Every screen of the flow writes — the licence check, the contact details, `POST /activities`,
 * the condition reports, the signatures, the return — and every one of those is `SalesDep` in
 * the API, so a read-only member is sent back to the booking before the screen renders rather
 * than being refused by the first thing they touch. Service staff outrank sales, so a loan
 * hand-over passes the same gate. The redirect replaces history: the URL that was refused is
 * not one to go back to.
 *
 * The routes carry a trailing underscore on the parameter (`$bookingId_`): each is a page of
 * its own and not a panel inside the booking detail, because a salesperson standing at the car
 * with a phone gets one column and one job.
 *
 * Spread the result into `createFileRoute`. A route with search parameters passes its schema in
 * as `search`, and gets it back as `validateSearch` beside the gate.
 */
export function handoverGate(): { beforeLoad: Gate }
export function handoverGate<S>(options: { search: S }): { beforeLoad: Gate; validateSearch: S }
export function handoverGate<S>(options?: { search: S }): { beforeLoad: Gate; validateSearch?: S } {
  const beforeLoad: Gate = ({ context, params }) => {
    if (cachedCan(context.queryClient, 'sell')) return
    // A walk-in has no record to be sent back to — no booking row exists — so it is sent to the
    // day, which is where its car is named (`Vehicles out now`).
    if (params.bookingId === undefined) throw redirect({ to: '/', replace: true })
    throw redirect({
      to: RECORD_ROUTE,
      params: { bookingId: params.bookingId },
      replace: true,
    })
  }
  return options === undefined ? { beforeLoad } : { beforeLoad, validateSearch: options.search }
}
